import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import { AssignmentReturn, Sync, Check, Close } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { transactionService } from '../services';

function formatCurrency(value: number | undefined): string {
  if (value == null) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatDate(date: string | undefined): string {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const DEBOUNCE_MS = 400;

const Refunds: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [siteTicketFilter, setSiteTicketFilter] = useState<'opened' | 'handled' | 'all'>('opened');
  const [searchEmail, setSearchEmail] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [refundDialog, setRefundDialog] = useState<{
    open: boolean;
    id: string;
    participantName: string;
    amount: number;
  } | null>(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundAmount, setRefundAmount] = useState<string>('');
  const [rejectDialog, setRejectDialog] = useState<{ id: string; name: string } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchEmail.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchEmail]);

  const { data: cancellationTicketsData, isLoading: loadingCancellationTickets } = useQuery({
    queryKey: ['cancellation-tickets', siteTicketFilter],
    queryFn: () => transactionService.listCancellationTickets(siteTicketFilter),
  });

  const { data: openedTicketsCountData } = useQuery({
    queryKey: ['cancellation-tickets', 'opened-count'],
    queryFn: () => transactionService.listCancellationTickets('opened'),
  });

  const { data: pendingRequestsData, isLoading: loadingRequests } = useQuery({
    queryKey: ['refund-requests', 'pending'],
    queryFn: () => transactionService.listRefundRequests('pending'),
  });

  const { data: paidData, isLoading: loadingPaid } = useQuery({
    queryKey: ['transactions', 'paid', debouncedSearch],
    queryFn: () =>
      transactionService.getAll({
        paymentStatus: 'paid',
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        limit: 100,
      }),
  });

  const { data: refundedData, isLoading: loadingRefunded } = useQuery({
    queryKey: ['transactions', 'refunded', debouncedSearch],
    queryFn: () =>
      transactionService.getAll({
        paymentStatus: 'refunded',
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        limit: 100,
      }),
  });

  const syncMutation = useMutation({
    mutationFn: () => transactionService.syncRefunded(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      const msg = data?.data?.updated
        ? `${data.data.updated} inscrição(ões) sincronizada(s) com Mercado Pago`
        : data?.message || 'Sincronização concluída';
      setSnackbar({ open: true, message: msg, severity: 'success' });
    },
    onError: (err: any) => {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Erro ao sincronizar',
        severity: 'error',
      });
    },
  });

  const refundMutation = useMutation({
    mutationFn: ({ id, reason, amount }: { id: string; reason: string; amount?: number }) =>
      transactionService.processRefund(id, reason, amount),
    onSuccess: () => {
      setRefundDialog(null);
      setRefundReason('');
      setRefundAmount('');
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['refund-requests'] });
      setSnackbar({ open: true, message: 'Reembolso processado com sucesso', severity: 'success' });
    },
    onError: (err: any) => {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Erro ao processar reembolso',
        severity: 'error',
      });
    },
  });

  const approveMutation = useMutation({
    mutationFn: (requestId: string) => transactionService.approveRefundRequest(requestId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['refund-requests'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      setSnackbar({
        open: true,
        message: data?.message || 'Solicitação aprovada e reembolso processado',
        severity: 'success',
      });
    },
    onError: (err: any) => {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Erro ao aprovar',
        severity: 'error',
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      transactionService.rejectRefundRequest(id, reason),
    onSuccess: () => {
      setRejectDialog(null);
      setRejectReason('');
      queryClient.invalidateQueries({ queryKey: ['refund-requests'] });
      setSnackbar({ open: true, message: 'Solicitação rejeitada', severity: 'success' });
    },
    onError: (err: any) => {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Erro ao rejeitar',
        severity: 'error',
      });
    },
  });

  const handleTicketMutation = useMutation({
    mutationFn: (ticketId: string) => transactionService.handleCancellationTicket(ticketId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cancellation-tickets'] });
      setSnackbar({ open: true, message: 'Pedido marcado como tratado', severity: 'success' });
    },
    onError: (err: any) => {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Erro ao marcar pedido',
        severity: 'error',
      });
    },
  });

  const cancellationTickets = cancellationTicketsData?.data ?? [];
  const pendingRequests = pendingRequestsData?.data ?? [];
  const paidTransactions = paidData?.data?.transactions ?? [];
  const refundedTransactions = refundedData?.data?.transactions ?? [];
  const openedTicketsCount = openedTicketsCountData?.data?.length ?? 0;

  const handleOpenRefund = (tx: any) => {
    setRefundDialog({
      open: true,
      id: tx._id,
      participantName: tx.participantName || tx.confirmationCode || 'Participante',
      amount: tx.amountPaid ?? 0,
    });
    setRefundAmount(String(tx.amountPaid ?? 0));
  };

  const handleConfirmRefund = () => {
    if (!refundDialog || !refundReason.trim()) {
      setSnackbar({ open: true, message: 'Informe o motivo do reembolso', severity: 'error' });
      return;
    }
    const amount = refundAmount ? parseFloat(refundAmount.replace(',', '.')) : undefined;
    refundMutation.mutate({ id: refundDialog.id, reason: refundReason.trim(), amount });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentReturn /> Reembolsos
        </Typography>
        <Button
          variant="outlined"
          startIcon={syncMutation.isPending ? <CircularProgress size={18} color="inherit" /> : <Sync />}
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
        >
          Sincronizar com Mercado Pago
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" allowScrollButtonsMobile>
            <Tab
              label={`Pedidos solicitados (${openedTicketsCount})`}
              value={0}
            />
            <Tab
              label={`Solicitações da casa (${pendingRequests.length})`}
              value={1}
            />
            <Tab label={`Pagas (${paidTransactions.length})`} value={2} />
            <Tab label={`Reembolsadas (${refundedTransactions.length})`} value={3} />
          </Tabs>

          {(tab === 2 || tab === 3) && (
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por email..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              sx={{ mb: 2 }}
              inputProps={{ 'aria-label': 'Buscar por email' }}
            />
          )}

          {tab === 0 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Pedidos da FAQ do evento. Abertura avisa suporte + organizador. A paróquia responde na
                aba Atendimento: aprovação (só se elegível + saldo) ou recusa (e-mail imediato). Sem
                resposta em 2 dias, cron diário alerta o suporte.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                {([
                  ['opened', 'Abertos'],
                  ['handled', 'Tratados'],
                  ['all', 'Todos'],
                ] as const).map(([value, label]) => (
                  <Chip
                    key={value}
                    label={label}
                    color={siteTicketFilter === value ? 'primary' : 'default'}
                    variant={siteTicketFilter === value ? 'filled' : 'outlined'}
                    onClick={() => setSiteTicketFilter(value)}
                    size="small"
                  />
                ))}
              </Box>
              {loadingCancellationTickets ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>E-mail</TableCell>
                        <TableCell>Evento</TableCell>
                        <TableCell>Motivo</TableCell>
                        <TableCell>Aberto em</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="center">Ação</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cancellationTickets.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            Nenhum pedido nesta lista
                          </TableCell>
                        </TableRow>
                      ) : (
                        cancellationTickets.map((ticket: any) => (
                          <TableRow key={ticket._id}>
                            <TableCell>{ticket.email || '-'}</TableCell>
                            <TableCell>
                              <Typography variant="body2">{ticket.eventTitle || '-'}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {ticket.eventSlug || ''}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 280 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  maxHeight: 96,
                                  overflow: 'auto',
                                }}
                                title={ticket.reason}
                              >
                                {ticket.reason || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>{formatDate(ticket.verifiedAt || ticket.createdAt)}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={ticket.status === 'handled' ? 'Tratado' : 'Aberto'}
                                color={ticket.status === 'handled' ? 'default' : 'warning'}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {ticket.status === 'opened' ? (
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={
                                    handleTicketMutation.isPending ? (
                                      <CircularProgress size={14} color="inherit" />
                                    ) : (
                                      <Check />
                                    )
                                  }
                                  disabled={handleTicketMutation.isPending}
                                  onClick={() => handleTicketMutation.mutate(String(ticket._id))}
                                >
                                  Marcar tratado
                                </Button>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(ticket.handledAt)}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {tab === 1 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Pedidos feitos pela casa (ex.: Zuadaria) no detalhe da inscrição. Aceitar processa o
                estorno no gateway (Pagar.me / Mercado Pago).
              </Typography>
              {loadingRequests ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Código</TableCell>
                        <TableCell>Participante</TableCell>
                        <TableCell>Evento / Casa</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell>Motivo</TableCell>
                        <TableCell>Solicitado</TableCell>
                        <TableCell align="center">Ação</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingRequests.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            Nenhuma solicitação pendente
                          </TableCell>
                        </TableRow>
                      ) : (
                        pendingRequests.map((req: any) => (
                          <TableRow key={req._id}>
                            <TableCell>{req.confirmationCode || '-'}</TableCell>
                            <TableCell>
                              <Typography variant="body2">{req.participantName || '-'}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {req.participantEmail || ''}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{req.eventTitle || '-'}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {req.parishName || '-'}
                                {req.brandKey === 'zuadaria' ? ' · Zuadaria' : ''}
                                {req.paymentProvider ? ` · ${req.paymentProvider}` : ''}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{formatCurrency(req.amount)}</TableCell>
                            <TableCell sx={{ maxWidth: 220 }}>
                              <Typography variant="body2" noWrap title={req.reason}>
                                {req.reason || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>{formatDate(req.requestedAt)}</TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  startIcon={
                                    approveMutation.isPending ? (
                                      <CircularProgress size={14} color="inherit" />
                                    ) : (
                                      <Check />
                                    )
                                  }
                                  disabled={approveMutation.isPending || rejectMutation.isPending}
                                  onClick={() => approveMutation.mutate(req._id)}
                                >
                                  Aceitar
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  startIcon={<Close />}
                                  disabled={approveMutation.isPending || rejectMutation.isPending}
                                  onClick={() =>
                                    setRejectDialog({
                                      id: req._id,
                                      name: req.participantName || req.confirmationCode || 'Participante',
                                    })
                                  }
                                >
                                  Rejeitar
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {tab === 2 && (
            <>
              {loadingPaid ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Código</TableCell>
                        <TableCell>Participante</TableCell>
                        <TableCell>Evento</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell>Data</TableCell>
                        <TableCell align="center">Ação</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paidTransactions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            Nenhuma transação paga encontrada
                          </TableCell>
                        </TableRow>
                      ) : (
                        paidTransactions.map((tx: any) => (
                          <TableRow key={tx._id}>
                            <TableCell>{tx.confirmationCode || '-'}</TableCell>
                            <TableCell>{tx.participantName || tx.participantEmail || '-'}</TableCell>
                            <TableCell>{tx.eventTitle || '-'}</TableCell>
                            <TableCell align="right">{formatCurrency(tx.amountPaid)}</TableCell>
                            <TableCell>{formatDate(tx.createdAt)}</TableCell>
                            <TableCell align="center">
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<AssignmentReturn />}
                                onClick={() => handleOpenRefund(tx)}
                              >
                                Reembolsar
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {tab === 3 && (
            <>
              {loadingRefunded ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Código</TableCell>
                        <TableCell>Participante</TableCell>
                        <TableCell>Evento</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell>Data reembolso</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {refundedTransactions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            Nenhum reembolso encontrado
                          </TableCell>
                        </TableRow>
                      ) : (
                        refundedTransactions.map((tx: any) => (
                          <TableRow key={tx._id}>
                            <TableCell>{tx.confirmationCode || '-'}</TableCell>
                            <TableCell>{tx.participantName || tx.participantEmail || '-'}</TableCell>
                            <TableCell>{tx.eventTitle || '-'}</TableCell>
                            <TableCell align="right">{formatCurrency(tx.refundAmount ?? tx.amountPaid)}</TableCell>
                            <TableCell>{formatDate(tx.refundedAt)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!refundDialog} onClose={() => setRefundDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar reembolso</DialogTitle>
        <DialogContent>
          {refundDialog && (
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography>
                Participante: <strong>{refundDialog.participantName}</strong>
              </Typography>
              <Typography>Valor original: {formatCurrency(refundDialog.amount)}</Typography>
              <TextField
                label="Motivo do reembolso *"
                multiline
                rows={3}
                fullWidth
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                required
              />
              <TextField
                label="Valor a reembolsar (opcional)"
                placeholder={formatCurrency(refundDialog.amount)}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                helperText="Deixe vazio para reembolso integral"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialog(null)}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmRefund}
            disabled={!refundReason.trim() || refundMutation.isPending}
          >
            {refundMutation.isPending ? <CircularProgress size={24} /> : 'Confirmar reembolso'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!rejectDialog} onClose={() => setRejectDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Rejeitar solicitação</DialogTitle>
        <DialogContent>
          {rejectDialog && (
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography>
                Participante: <strong>{rejectDialog.name}</strong>
              </Typography>
              <TextField
                label="Motivo da rejeição (opcional)"
                multiline
                rows={3}
                fullWidth
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(null)}>Voltar</Button>
          <Button
            variant="contained"
            color="error"
            disabled={rejectMutation.isPending}
            onClick={() => {
              if (!rejectDialog) return;
              rejectMutation.mutate({ id: rejectDialog.id, reason: rejectReason.trim() });
            }}
          >
            {rejectMutation.isPending ? <CircularProgress size={22} /> : 'Rejeitar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Refunds;
